import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, Clock, CheckCircle2, Plane, Briefcase, CalendarClock, Thermometer } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/Page";

function StatCard({ icon: Icon, label, value, accent, testid, to }) {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <Card
      className={`p-5 border-slate-200 rounded-xl transition-all ${clickable ? "cursor-pointer hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5" : ""}`}
      data-testid={testid}
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(to); } } : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-slate-400"}`} />
      </div>
      <div className="text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </Card>
  );
}

function elapsed(startAt) {
  return Math.max(0, Math.floor((Date.now() - new Date(startAt).getTime()) / 1000));
}
function fmtClock(sec) {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [, force] = useState(0);
  const tick = useRef(null);

  const load = async () => setData((await api.get("/admin/live")).data);
  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    tick.current = setInterval(() => force((n) => n + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick.current); };
  }, []);

  const c = data?.counts || {};

  return (
    <div>
      <PageHeader title="Live-Dashboard" subtitle="Echtzeit-Überblick über dein Team." testid="dashboard-header" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Clock} label="Arbeiten gerade" value={c.working ?? "–"} accent="text-emerald-500" testid="stat-working" />
        <StatCard icon={Users} label="Mitarbeiter" value={c.total_employees ?? "–"} testid="stat-employees" to="/app/mitarbeiter" />
        <StatCard icon={CheckCircle2} label="Offene Zeit-Freigaben" value={c.pending_sessions ?? "–"} accent="text-purple-500" testid="stat-pending-sessions" to="/app/freigaben" />
        <StatCard icon={Plane} label="Offene Urlaubsanträge" value={c.pending_vacations ?? "–"} accent="text-blue-500" testid="stat-pending-vacations" to="/app/freigaben" />
        <StatCard icon={Briefcase} label="Offene Aufträge" value={c.open_orders ?? 0} testid="stat-open-orders" to="/app/auftraege" />
        <StatCard icon={CalendarClock} label="Heute fällig" value={c.due_today ?? 0} accent="text-amber-500" testid="stat-due-today" to="/app/auftraege" />
        <StatCard icon={Thermometer} label="Heute krank" value={c.sick_today ?? 0} accent="text-rose-500" testid="stat-sick-today" to="/app/abwesenheiten" />
      </div>

      <Card className="border-slate-200 rounded-xl p-5" data-testid="who-is-working">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <h3 className="font-semibold text-slate-900">Wer arbeitet gerade</h3>
        </div>
        {(!data?.working || data.working.length === 0) ? (
          <EmptyState icon={Clock} title="Aktuell arbeitet niemand" testid="working-empty" />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.working.map((w) => (
              <div key={w.user_id} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid={`working-${w.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm">{w.name?.charAt(0)}</div>
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{w.name}</div>
                    <div className="text-xs text-emerald-700">seit {w.start} Uhr</div>
                  </div>
                </div>
                <div className="font-mono font-bold text-emerald-700">{w.start_at ? fmtClock(elapsed(w.start_at)) : ""}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
